<template>
  <AIVDialog :visible.sync="dialogVisible" title="Send Compliance Forms" width="600px">
    <div v-if="clientId" class="send-compliance-form">
      <p class="send-compliance-form__subtitle">Sending to: <strong>{{ clientName }}</strong></p>

      <div v-if="loadingForms" class="text-center py-3">
        <i class="fa fa-spinner fa-spin" style="color: #fff;"></i>
        <span style="color: #fff;"> Loading forms...</span>
      </div>

      <div v-else class="send-compliance-form__fields">
        <div class="send-compliance-form__field">
          <label class="field-label">Signer Email</label>
          <el-input v-model="signerEmail" placeholder="email@example.com" />
        </div>
        <div class="send-compliance-form__field">
          <label class="field-label">Signer Name</label>
          <el-input v-model="signerName" placeholder="Full legal name" />
        </div>
        <div class="send-compliance-form__field">
          <label class="field-label">Relationship</label>
          <AIVSelect
            v-model="signerRelationship"
            :options="relationshipOptions"
            :searchable="false"
            :clearable="false"
            placeholder="Select..."
          />
        </div>
        <div class="send-compliance-form__field">
          <label class="field-label">Signature Type</label>
          <el-radio-group v-model="signatureType" size="small">
            <el-radio-button label="independent">Independent (Client Only)</el-radio-button>
            <el-radio-button label="guardian">Has Guardian</el-radio-button>
          </el-radio-group>
        </div>
        <template v-if="signatureType === 'guardian'">
          <div class="send-compliance-form__field">
            <label class="field-label">Guardian Name <span class="send-compliance-form__required">*</span></label>
            <el-input v-model="guardianName" placeholder="Guardian full legal name" />
          </div>
          <div class="send-compliance-form__field">
            <label class="field-label">Guardian Email <span class="send-compliance-form__required">*</span></label>
            <el-input v-model="guardianEmail" placeholder="guardian@example.com" />
          </div>
        </template>

        <!-- Variable Rate Field (for Fee Agreement / Consent) -->
        <div class="send-compliance-form__field" v-if="selectedFormIds.some(id => hasFormName(id, 'Fee Agreement')) || selectedFormIds.some(id => hasFormName(id, 'Consent'))">
          <label class="field-label">Rate Amount</label>
          <el-input v-model="rateAmount" placeholder="e.g. $25.00/hr" />
          <span style="font-size: 11px; color: #999;">Merged into Fee Agreement and Consent documents</span>
        </div>

        <!-- Media Release Checklist -->
        <div class="send-compliance-form__field" v-if="selectedFormIds.some(id => hasFormName(id, 'Media Release'))">
          <label class="field-label">Media Release Permissions</label>
          <el-checkbox-group v-model="mediaPermissions">
            <el-checkbox label="Social Media" class="form-checkbox" />
            <el-checkbox label="Website" class="form-checkbox" />
            <el-checkbox label="Print Materials" class="form-checkbox" />
            <el-checkbox label="Video/Film" class="form-checkbox" />
            <el-checkbox label="Photography" class="form-checkbox" />
            <el-checkbox label="Newsletters" class="form-checkbox" />
          </el-checkbox-group>
        </div>

        <div class="send-compliance-form__field">
          <label class="field-label">Forms to Send</label>
          <el-checkbox-group v-model="selectedFormIds">
            <el-checkbox
              v-for="f in forms"
              :key="f.id"
              :label="f.id"
              class="form-checkbox"
            >
              {{ f.display_name }}
            </el-checkbox>
          </el-checkbox-group>
          <el-button type="text" size="small" @click="toggleSelectAll" class="mt-1">
            {{ allSelected ? 'Deselect All' : 'Select All' }}
          </el-button>
        </div>
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="sendForms" :loading="sending">
          <i class="fa fa-paper-plane"></i> Send
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import { complianceFormsService } from "@/serviceHandlers/complianceFormsService";

export default {
  name: "SendComplianceFormsModal",
  props: {
    visible: { type: Boolean, default: false },
    clientId: { type: [Number, String], default: null },
    clientName: { type: String, default: "" },
  },
  data() {
    return {
      signerEmail: "",
      signerName: "",
      signerRelationship: "self",
      signatureType: "independent",
      guardianEmail: "",
      guardianName: "",
      rateAmount: "",
      mediaPermissions: [],
      selectedFormIds: [],
      sending: false,
      forms: [],
      loadingForms: false,
      relationshipOptions: [
        { id: "self", name: "Self" },
        { id: "guardian", name: "Guardian" },
        { id: "responsible_party", name: "Responsible Party" },
      ],
    };
  },
  computed: {
    dialogVisible: {
      get() { return this.visible; },
      set(val) { this.$emit("update:visible", val); },
    },
    allSelected() {
      return this.forms.length > 0 && this.selectedFormIds.length === this.forms.length;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.signerEmail = "";
        this.signerName = "";
        this.signatureType = "independent";
        this.guardianEmail = "";
        this.guardianName = "";
        this.rateAmount = "";
        this.mediaPermissions = [];
        this.selectedFormIds = [];
        this.fetchForms();
      }
    },
  },
  methods: {
    async fetchForms() {
      this.loadingForms = true;
      try {
        const data = await complianceFormsService.list();
        this.forms = Array.isArray(data) ? data : data.rows || [];

        // Pre-select based on per-client config if available
        if (this.clientId) {
          try {
            const config = await complianceFormsService.getClientFormConfig(this.clientId);
            if (config.configured) {
              const configuredIds = config.forms.map((f) => f.id);
              this.selectedFormIds = this.forms.filter((f) => configuredIds.includes(f.id)).map((f) => f.id);
            } else {
              this.selectedFormIds = this.forms.map((f) => f.id);
            }
          } catch {
            this.selectedFormIds = this.forms.map((f) => f.id);
          }
        } else {
          this.selectedFormIds = this.forms.map((f) => f.id);
        }
      } catch (err) {
        this.$notifyError("Failed to load compliance forms.");
      } finally {
        this.loadingForms = false;
      }
    },
    toggleSelectAll() {
      this.selectedFormIds = this.allSelected ? [] : this.forms.map((f) => f.id);
    },
    hasFormName(formId, name) {
      const form = this.forms.find(f => f.id === formId);
      return form && form.display_name && form.display_name.toLowerCase().includes(name.toLowerCase());
    },
    async sendForms() {
      if (!this.signerEmail) {
        this.$notifyError("Signer email is required.");
        return;
      }
      if (this.signatureType === 'guardian' && !this.guardianEmail) {
        this.$notifyError("Guardian email is required when guardian signature is enabled.");
        return;
      }
      if (this.signatureType === 'guardian' && !this.guardianName) {
        this.$notifyError("Guardian name is required.");
        return;
      }
      if (this.selectedFormIds.length === 0) {
        this.$notifyError("Select at least one form.");
        return;
      }

      this.sending = true;
      try {
        const guardianPayload = this.signatureType === 'guardian'
          ? { guardianEmail: this.guardianEmail, guardianName: this.guardianName }
          : {};

        await complianceFormsService.sendPortal({
          clientId: this.clientId,
          signerEmail: this.signerEmail,
          signerName: this.signerName,
          signerRelationship: this.signerRelationship,
          signatureType: this.signatureType,
          formIds: this.selectedFormIds,
          rateAmount: this.rateAmount || undefined,
          mediaPermissions: this.mediaPermissions.length ? this.mediaPermissions : undefined,
          ...guardianPayload,
        });

        this.$notifySuccess(`${this.selectedFormIds.length} form(s) sent successfully!`);
        this.$emit("sent");
        this.dialogVisible = false;
      } catch (error) {
        this.$notifyError("Error sending forms: " + (error.response?.data?.message || error));
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.send-compliance-form__subtitle {
  color: #e0e0e0;
  margin-bottom: 1rem;
}

.send-compliance-form {
  max-height: calc(80vh - 160px);
  overflow-y: auto;
}

.send-compliance-form__fields {
  display: flex;
  flex-direction: column;
}

.send-compliance-form__field {
  margin-bottom: 1rem;
}

.field-label {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 4px 0;
  display: block;
}

.send-compliance-form__required {
  color: #f56c6c;
}

.form-checkbox {
  display: block;
  margin-left: 0 !important;
  margin-bottom: 0.25rem;
}

:deep(.form-checkbox .el-checkbox__label) {
  color: #fff !important;
  font-size: 14px;
}

:deep(.el-checkbox__label) {
  color: #fff !important;
}
</style>
